import { PaymentInfo } from '@scopely/web-sdk-payments/dist/src/model/payment.model';
import { UseMutateFunction } from '@tanstack/react-query';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { SysUIParameter } from '@wwe-portal/web/libs/analytics/common';
import { InterstitialOffer, UpsellOffer } from '../client/interstitial';

export class SubscriptionRedirected extends Error {}

export const PaymentStatusEnum = {
  NONE: 'NONE',
  CHECKING: 'CHECKING',
  PAYING: 'PAYING',
  DONE: 'DONE',
} as const;

export enum AlternativePurchaseTypes {
  Avatar = 'avatar',
  Frame = 'frame',
  Energy = 'energy',
  Daily = 'daily',
}

export enum TransactionTypes {
  Payment = 'payment',
  Reward = 'reward',
  WebCoin = 'webCoin',
  ClubCoin = 'clubCoin',
  PaymentCompleted = 'paymentCompleted',
  ClaimAll = 'claimAll',
}

export type PaymentStatusType = keyof typeof PaymentStatusEnum;

export type UsePurchaseHook = () => {
  paymentStatus: PaymentStatusType;
  closePayment: () => void;
  purchase: ({
    sku,
    offerId,
    packageId,
    version,
    redirectUri,
  }: {
    sku: string | undefined;
    offerId: string | undefined;
    packageId: string | undefined;
    version?: string;
    redirectUri: string;
  }) => Promise<boolean | undefined>;
};

export interface PurchasingOffer {
  offerData: OfferData;
  offerType?: string;
  paymentId?: string;
  transactionId?: string;
  offerId?: string | number;
  paymentInfo?: PaymentInfo;
  price?: number;
  alternativePurchaseType?: AlternativePurchaseTypes;
  isPaymentConflicted?: boolean;
  analyticsData?: Partial<SysUIParameter>;
  transactionType?: TransactionTypes;
  count?: number;
}

export interface UpsellData {
  intersititalData: UpsellOffer | InterstitialOffer;
  offerId: string;
}

export interface BasePurchasingOffer {
  offerData: OfferData;
  upsellData?: UpsellData;
  paymentId?: string;
  transactionId?: string;
  analyticsData?: SysUIParameter;
}

export interface UsePurchaseOfferProps<T extends BasePurchasingOffer> {
  verifyPaymentFn: (data: T) => Promise<void>;
  verifyOfferFn: (data: T) => Promise<void>;
  purchaseFn: (data: T) => Promise<void>;

  refetchStatusInterval: number;
  onPaymentError: (error?: unknown) => Promise<void>;

  onVerifyingPostPayment?: (purchasingOffer: T | null | undefined) => void;
  onPaymentSuccess?: () => void;
  getPurchasingOffer: () => T | null | undefined;
  persistPurchasingOffer: (data: T | null | undefined) => void;
  clearPurchasingOffer: () => void;
}

export interface UsePurchaseOfferResult<T extends BasePurchasingOffer> {
  startPurchaseMutate: UseMutateFunction<void, unknown, T, unknown>;
  verifyMutate: UseMutateFunction<void, unknown, T, unknown>;
  isPostPaymentVerifying: boolean;
  isPreparingPayment: boolean;
  startCancelPayment?: UseMutateFunction<void, unknown, void, unknown>;
  isCancelingPayment?: boolean;
}
